import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/nutritionist-real.jpg";

export const nutritionists: Persona = {
  slug: "nutritionists",
  profession: { hu: "táplálkozási tanácsadó", en: "nutritionist" },
  professionPlural: { hu: "táplálkozási tanácsadók", en: "nutritionists" },
  professionGenitive: {
    hu: "táplálkozási tanácsadóként",
    en: "as a nutritionist",
  },
  heroH1: {
    hu: "Diéták, csomagok, követés: egy chat üzenetből.",
    en: "Diets, packages, tracking: from one chat message.",
  },
  heroSub: {
    hu: "AI weboldal, online konzultáció, étrend-csomagok és haladás-követés: egy platformon táplálkozási tanácsadóknak és dietetikusoknak.",
    en: "AI website, online consultations, meal-plan packages and progress tracking: one platform for nutritionists and dietitians.",
  },
  heroImage,
  heroImageAlt: "Nutritionist running a consultation",
  chatExamplePrompt: {
    hu: "Készíts étrendi tanácsadás csomagot, 4 alkalom 60 000 Ft",
    en: "Create a nutrition coaching package, 4 sessions, 60,000 HUF",
  },
  chatExampleResponse: {
    hu: "Létrehoztam: 4 alkalmas étrendi tanácsadás csomag, 4 × 60 perc konzultáció + személyre szabott étrend, 60 000 Ft. Élesítve a weboldalon.",
    en: "Created: 4-session nutrition coaching package, 4 × 60-minute consults + personalized plan, 60,000 HUF. Live on the site.",
  },
  accentColor: "lime",

  defaultMode: "solo",
  teamSize: { min: 1, max: 6 },
  soloLabel: { hu: "Szóló tanácsadó", en: "Solo nutritionist" },
  teamLabel: {
    hu: "Táplálkozási csapat / klinika",
    en: "Nutrition team / clinic",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Az AI riportot küld: 2 új lead a weboldalról, mindketten kértek bevezető konzultációt.",
        en: "AI report: 2 new leads from the site, both requested an intro consult.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "60 perc online konzultáció Annával. Az ő haladás-grafikonja, étkezési naplója már megnyitva.",
        en: "60-minute online consult with Anna. Her progress chart and food diary are already open.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új csomagot hozol létre chatben: '8 hetes anyagcsere-újraindítás, 95 000 Ft'. 30 mp múlva élesítve.",
        en: "You create a new package in chat: '8-week metabolism reset, 95,000 HUF'. Live in 30 seconds.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Egy kliens fizet részletben. A 2. részlet automatikusan levonódik, számla generálva.",
        en: "A client pays in installments. The 2nd installment auto-deducts, invoice generated.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Napzárás: 5 konzultáció, 1 új csomag, 78 000 Ft bevétel. Étrend-tervek küldve.",
        en: "End of day: 5 consults, 1 new package, 78,000 HUF revenue. Meal plans sent.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Klinika dashboard: 3 tanácsadód 12 konzultációt tart ma. Mindenki naptára átláthatóan.",
        en: "Clinic dashboard: your 3 nutritionists have 12 consults today. All calendars at a glance.",
      },
    },
    {
      time: "10:30",
      what: {
        hu: "Új kliens cukorbeteg étrendre: az AI a specializált tanácsadóhoz (Évához) osztja.",
        en: "New client needing a diabetic diet: AI routes to the specialist (Éva).",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy tanácsadó beteg. Az AI áthelyezi 3 konzultációját másik kollégához, klienseket értesít.",
        en: "A nutritionist is sick. AI moves their 3 consults to another colleague, notifies clients.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI mutatja: a sport-táplálkozás konverzió +28% a héten. Megmutatod a csapatnak.",
        en: "AI shows: sports-nutrition conversion is +28% this week. You share it with the team.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Heti riport: csomag-eladás, kliens-haladás, várólista. Marketing AI javaslatok az újabb hétre.",
        en: "Weekly report: package sales, client progress, waitlist. Marketing AI suggestions for the next week.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel tanácsadónként, jutalék-elszámolás, számlák kiállítva.",
        en: "Revenue per nutritionist, commission ready, invoices issued.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Minden egy fiókban", en: "Everything in one account" },
      body: {
        hu: "Naptár, kliens-előzmény, étrend-tervek, számla: egyetlen rendszerben, váltogatás nélkül.",
        en: "Calendar, client history, meal plans, invoices: in one system, no switching.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Előkérdőív + automata ajánlat",
        en: "Intake form + auto-quote",
      },
      body: {
        hu: "Új érdeklődő kitölti az online kérdőívet, automatikusan kap egy ajánlott csomagot és foglalási linket: te csak a bevezetőre készülsz.",
        en: "New leads fill out the online form and auto-receive a recommended package and booking link: you just prep the intro.",
      },
    },
    {
      icon: "Apple",
      title: {
        hu: "Étrend-sablonok és követés",
        en: "Meal-plan templates and tracking",
      },
      body: {
        hu: "Mentett étrend-sablonok, kliens-személyre szabás, mobilon átadás.",
        en: "Saved meal-plan templates, personalized per client, delivered on mobile.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan tanácsadó, egy ár",
        en: "Unlimited nutritionists, one price",
      },
      body: {
        hu: "1 vagy 6 fő: ugyanaz a havidíj. Mindenki saját naptárral és kliens-bázissal.",
        en: "1 or 6: same monthly fee. Each with their own calendar and clients.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Egészségügyi adatokhoz csak az érintett tanácsadó fér hozzá.",
        en: "Only the assigned nutritionist accesses medical data.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a klienseket", en: "AI assigns clients" },
      body: {
        hu: "Új lead: az AI specialitás (cukorbeteg, sport, gyermek-étrend) alapján a megfelelő tanácsadóhoz osztja.",
        en: "New lead: AI assigns by specialty (diabetic, sports, child nutrition) to the right nutritionist.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Jutalék és haladás-statisztika",
        en: "Commission and progress stats",
      },
      body: {
        hu: "Ki mennyi klienst vitt eredményre, melyik csomag teljesít legjobban: exportálható.",
        en: "Who got how many clients to results, which package performs best: exportable.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: {
        hu: "Smart booking és emlékeztetők",
        en: "Smart booking and reminders",
      },
      body: {
        hu: "Időzóna-kezelés, buffer time, no-show védelem előleggel, automata SMS/email/WhatsApp emlékeztető.",
        en: "Timezone handling, buffer time, no-show protection with deposits, auto SMS/email/WhatsApp reminders.",
      },
    },
    {
      icon: "Briefcase",
      title: { hu: "Csomag-értékesítés", en: "Package sales" },
      body: {
        hu: "4 alkalmas, 8 hetes, 12 hetes csomagok: egy oldalon, online fizetéssel.",
        en: "4-session, 8-week, 12-week packages: one page, online payment.",
      },
    },
    {
      icon: "CreditCard",
      title: {
        hu: "Bérletek és online fizetés",
        en: "Passes and online payment",
      },
      body: {
        hu: "Bankkártya, Apple/Google Pay foglaláskor, helyszíni készpénz is: minden tranzakció automata számlával.",
        en: "Card, Apple/Google Pay at booking, in-person cash too: every transaction auto-invoiced.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI follow-up kampányok", en: "AI follow-up campaigns" },
      body: {
        hu: "Kliens 2 hete nem jelentkezett? Az AI automatikus check-int küld, motivál a visszatérésre: te állítod be, a rendszer kiküldi.",
        en: "Client hasn't checked in for 2 weeks? AI sends an automatic check-in, motivates them back: you configure, the system sends.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "Csapatban dolgozunk klinikán: hogy kezeli?",
        en: "We work as a team in a clinic: how does it handle it?",
      },
      answer: {
        hu: "Minden tanácsadónak saját naptára, kliens-bázisa, étrend-sablonjai vannak. Te tulajdonosként a centralizált dashboardon mindent látsz, beleértve a kliens-haladást aggregáltan. Korlátlan tanácsadót vehetsz fel, NEM fizetsz többet érte (1 vagy 6 fő: ugyanaz a 10 000 Ft + ÁFA / hó).",
        en: "Each nutritionist has their own calendar, clients and meal-plan templates. As owner you see everything on the central dashboard including aggregated client progress. Add unlimited nutritionists, you do NOT pay more (1 or 6, same 10,000 HUF + VAT / month).",
      },
    },
    {
      question: {
        hu: "Egészségügyi adatokat kezelek: biztonságos?",
        en: "I handle medical data: is it secure?",
      },
      answer: {
        hu: "Igen. EU-szerverek, GDPR-megfelelőség, end-to-end titkosítás. Jogosultság-kezeléssel csak az érintett tanácsadó látja a saját klienseit. Az adatok soha nem kerülnek 3. félhez, AI training-re sem.",
        en: "Yes. EU servers, GDPR-compliant, end-to-end encryption. Role-based access: only the assigned nutritionist sees their clients. Data is never shared with 3rd parties, never used for AI training.",
      },
    },
    {
      question: {
        hu: "Sok kliens elkezdi, de pár hét után eltűnik",
        en: "Many clients start but disappear after a few weeks",
      },
      answer: {
        hu: "Pont ezért van az AI follow-up. Ha a kliens 2 hete nem jelentkezett, automatikus check-int küld ('Hogy halad az étrend? Van kérdés?'). Bizonyítottan +35% retention.",
        en: "That's exactly why we have AI follow-up. If a client doesn't check in for 2 weeks, an automatic check-in goes out ('How's the diet going? Questions?'). Proven +35% retention.",
      },
    },
    {
      question: {
        hu: "Étrend-sablonjaim Word-ben vannak: át tudom hozni?",
        en: "My meal templates are in Word: can I bring them over?",
      },
      answer: {
        hu: "Igen. Másold át a Bespire sablon-rendszerbe (Word, PDF, vagy chat-ben átolvasod az AI-nak), és minden klientre 5 perc alatt személyre szabható.",
        en: "Yes. Copy them into Bespire's template system (Word, PDF, or read them to the AI in chat), and they're personalizable per client in 5 minutes.",
      },
    },
  ],

  monetization: [
    {
      icon: "Heart",
      title: { hu: "Egyéni konzultáció", en: "Individual consultation" },
      examples: [
        { hu: "Bevezető 60 perc", en: "Intro 60 min" },
        { hu: "Kontroll 30 perc", en: "Follow-up 30 min" },
        { hu: "Online konzultáció", en: "Online consult" },
      ],
      priceHint: {
        hu: "Példa: 40 konzultáció × 16 000 Ft = 640 000 Ft / hó",
        en: "Example: 40 consults × 16,000 HUF = 640,000 HUF / month",
      },
    },
    {
      icon: "Apple",
      title: { hu: "Étrend-csomagok", en: "Meal-plan packages" },
      examples: [
        { hu: "4 alkalmas alaprend", en: "4-session basics" },
        { hu: "8 hetes anyagcsere-program", en: "8-week metabolism program" },
        { hu: "12 hetes átalakulás", en: "12-week transformation" },
      ],
      priceHint: {
        hu: "Példa: 10 csomag × 75 000 Ft = 750 000 Ft / hó",
        en: "Example: 10 packages × 75,000 HUF = 750,000 HUF / month",
      },
    },
    {
      icon: "Activity",
      title: { hu: "Specializált programok", en: "Specialized programs" },
      examples: [
        { hu: "Cukorbeteg étrend", en: "Diabetic diet" },
        {
          hu: "Sport-táplálkozás (versenyzőknek)",
          en: "Sports nutrition (athletes)",
        },
        { hu: "Pre/postnatal program", en: "Pre/postnatal program" },
      ],
      priceHint: {
        hu: "Példa: 8 program × 95 000 Ft = 760 000 Ft / hó",
        en: "Example: 8 programs × 95,000 HUF = 760,000 HUF / month",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Digitális anyagok", en: "Digital materials" },
      examples: [
        { hu: "Étrend-tervező e-book", en: "Meal planner e-book" },
        { hu: "Receptkönyv (50 recept)", en: "Recipe book (50 recipes)" },
        { hu: "Felvett online kurzus", en: "Recorded online course" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~140 000 Ft / hó",
        en: "Passive revenue: ~140,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Tagság és bérletek", en: "Membership and passes" },
      examples: [
        { hu: "Havi étrend-frissítés", en: "Monthly meal plan update" },
        { hu: "VIP support tagság", en: "VIP support membership" },
        { hu: "Éves követő program", en: "Annual follow-up program" },
      ],
      priceHint: {
        hu: "Klinika-szint: 3 tanácsadó × 20 tagsági kliens × 22 000 Ft = 1 320 000 Ft / hó potenciál",
        en: "Clinic level: 3 nutritionists × 20 member clients × 22,000 HUF = 1,320,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+55%",
      label: { hu: "8-hetes program eladás", en: "8-week program sales" },
    },
    {
      value: "3.2x",
      label: { hu: "kliens-retention", en: "client retention" },
    },
    {
      value: "4.9★",
      label: { hu: "kliens-elégedettség", en: "client satisfaction" },
    },
    {
      value: "5 perc",
      label: { hu: "étrend-PDF generálás", en: "meal-plan PDF generation" },
    },
  ],
  testimonial: {
    quote: {
      hu: "A 8-hetes programjaim eladása 50%-kal nőtt, és az étrend-tervek generálása sablonok alapján pár percre csökkent. Időm maradt a kliensekre.",
      en: "My 8-week program sales grew 50%, and meal-plan generation from templates dropped to minutes. I have time for clients now.",
    },
    name: "Pintér Júlia",
    role: { hu: "Táplálkozási tanácsadó", en: "Nutritionist" },
    city: { hu: "Eger", en: "Eger" },
    avatarInitials: "PJ",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a szolgáltatásaidat",
        en: "Tell us your services in chat",
      },
      body: {
        hu: "Konzultáció, étrend-csomag, sport-táplálkozás: az AI strukturálja.",
        en: "Consultation, meal-plan packages, sports nutrition: AI structures it.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a tanácsadói oldalt",
        en: "AI builds your nutritionist site",
      },
      body: {
        hu: "Csomagok, foglalás, kérdőív, kliens-portál.",
        en: "Packages, booking, intake form, client portal.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Migráljuk a kliens-adatokat",
        en: "We migrate client data",
      },
      body: {
        hu: "Korábbi étrendek, mérések, fejlődés: egy profilban.",
        en: "Past meal plans, measurements, progress: in one profile.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő, online foglalás + étrend-generátor",
        en: "Live, online booking + meal-plan generator",
      },
      body: {
        hu: "Sablonok alapján AI-asszisztált PDF-generálás.",
        en: "AI-assisted PDF generation from templates.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Google Meet",
    "Stripe",
    "SimplePay",
    "WhatsApp Business",
    "Mailchimp",
  ],
  breadcrumbLabel: { hu: "Táplálkozási tanácsadók", en: "Nutritionists" },
  similarPersonas: [
    "fitness-personal-trainers",
    "psychologists-mentors",
    "therapists-coaches",
  ],
};
